import { UntypedFormGroup, AbstractControl } from '@angular/forms';
import { StoreService } from 'core/services/store.service';
import { Subscription } from 'rxjs';

export class QuestionBase<T> {
  private _id: string;
  private _action: string;
  private _childQuestions: any[];
  private tooltipSubscription: Subscription;
  designSystem: boolean;
  value: T;
  key: string;
  label: string;
  tooltip: string;
  tooltipKey: string;
  hideLabel: boolean;
  placeholder: string;
  isGroup: boolean = false;
  required: boolean;
  hideRequiredMark: boolean;
  clickTags: boolean;
  editable: boolean;
  disabled: boolean;
  hidden: boolean;
  order: number;
  controlType: string;
  class: string;
  eventHandlers: any;
  validators: any;
  errors: any[];
  form: UntypedFormGroup;
  control: AbstractControl;
  iconClass: any;
  labelClass: any;
  valueClass: any;
  buttonType: any;
  size: string;
  title: string;
  subtitle: string;
  tabs: any;
  error: any;
  hideError: any;
  options: any;
  switchClass: any;
  switchOffLabel: string;
  switchOnLabel: string;
  dataProvider: any;
  selected: any;
  agents: any;
  info: any;
  groups: any;
  courses: any;
  unavailableCourses: any;
  copy: any;
  getData: any;
  owner: any;
  courseId: any;
  maxSizeMb?: number;
  feature?: string;
  parent: QuestionBase<any>;
  isSection?: boolean;
  isDropdown?: boolean;
  disableGrid?: boolean;
  locale?: any;
  protected _timezone?: any;
  protected _minDate?: any;
  protected _maxDate?: any;
  protected _disableDates?: any;
  mode?: any;
  defaultHour?: any;
  defaultMinute?: any;
  defaultSeconds?: any;
  enableTime?: any;
  inline?: any;
  multiple?: any;
  secondary?: any;
  big?: any;
  entityOptions?: any;
  open?: any;
  heightBox?: number;
  separator?: boolean;
  selector?: boolean;
  optionLabel?: string;
  hideGroup?: boolean;
  hideButtons?: boolean;
  isDefaultClass?: boolean;
  kpisQuestions?: any;
  kpisTranslatedQuestions?: any;
  languageQuestion?: any;
  languageTranslatedQuestion?: any;
  isTranslationModal?: any;
  hasHtml?: boolean;

  constructor(options: any = {}) {
    const {
      value,
      key,
      label,
      tooltip,
      tooltipKey,
      hideLabel,
      isGroup,
      required,
      hideRequiredMark,
      placeholder,
      editable = true,
      iconClass,
      designSystem = false,
      size,
      clickTags,
      disabled,
      hidden,
      order,
      controlType,
      class: classString,
      eventHandlers,
      childQuestions,
      validators,
      maxSizeMb,
      errors,
      feature,
      title,
      subtitle,
      isSection,
      isDropdown,
      locale,
      disableGrid,
      timezone,
      minDate,
      maxDate,
      disableDates,
      mode,
      defaultHour,
      defaultMinute,
      defaultSeconds,
      enableTime,
      inline,
      multiple,
      secondary,
      big,
      entityOptions,
      open,
      heightBox,
      separator,
      selector,
      optionLabel,
      hideGroup,
      hideButtons,
      isDefaultClass,
      hasHtml
    } = options;
    this.value = value;
    this.key = key || '';
    this.placeholder = placeholder || 'actions.writeHere';
    // this.id = id || `_${UtilsService.createGuid()}`;
    this.label = label || '';
    this.title = title || '';
    this.subtitle = subtitle || '';
    this.tooltip = tooltip;
    this.tooltipKey = tooltipKey;
    this.hideLabel = hideLabel || false;
    this.isGroup = isGroup || false;
    this.required = !!required;
    this.hideRequiredMark = !!hideRequiredMark;
    this.editable = editable;
    this.iconClass = iconClass;
    this.designSystem = designSystem;
    this.clickTags = !!clickTags;
    this.disabled = !!disabled;
    this.hidden = !!hidden;
    this.order = order === undefined ? 1 : order;
    this.controlType = controlType || '';
    this.maxSizeMb = maxSizeMb;
    this.size = size;

    this.class = classString || '';
    this.eventHandlers = eventHandlers || {};
    this.childQuestions = childQuestions || [];
    this.validators = validators || {};
    this.errors = errors;
    this.feature = feature;
    this.isSection = isSection;
    this.isDropdown = isDropdown;
    this.disableGrid = disableGrid;
    this.locale = locale;
    this.timezone = timezone;
    this.minDate = minDate;
    this.maxDate = maxDate;
    this.disableDates = disableDates;
    this.mode = mode;
    this.defaultHour = defaultHour;
    this.defaultMinute = defaultMinute;
    this.defaultSeconds = defaultSeconds;
    this.enableTime = enableTime;
    this.inline = inline;
    this.multiple = multiple;
    this.secondary = secondary;
    this.big = big;
    this.entityOptions = entityOptions;
    this.open = open;
    this.heightBox = heightBox;
    this.separator = separator;
    this.selector = selector;
    this.optionLabel = optionLabel;
    this.hideGroup = hideGroup;
    this.hideButtons = hideButtons;
    this.isDefaultClass = isDefaultClass;
    this.hasHtml = hasHtml;
  }

  get id() {
    const parentId = this.parent ? this.parent.id : '';
    return this.key ? `${parentId || this.action}_${this.key}` : '';
  }

  set childQuestions(childQuestions) {
    if (childQuestions) {
      childQuestions
        .filter((child) => child)
        .forEach((child) => {
          child.parent = this;
        });
      this._childQuestions = childQuestions;
    } else {
      this._childQuestions = [];
    }
  }

  get childQuestions() {
    return this._childQuestions;
  }

  get action(): string {
    return this._action;
  }

  set action(value: string) {
    this._action = value;
    if (this.action && (this.key || this.tooltipKey) && !this.tooltipSubscription) {
      this.tooltipSubscription = StoreService.get(this.action).subscribe((data) => {
        const key = this.tooltipKey || this.key;
        if (data && data[key]) {
          this.tooltip = data[key];
        } else {
          // eslint-disable-next-line
          console.warn(`tooltip not found: ${this.action}.${key}`);
        }
      });
    }
  }

  getChildQuestion(questionKey: string) {
    if (questionKey) {
      return this.childQuestions.find((question) => question && question.key === questionKey);
    }
  }

  onDeleteQuestion() {
    if (this.tooltipSubscription) {
      this.tooltipSubscription.unsubscribe();
    }
  }

  parseValue(value) {
    return value;
  }

  updateChildQuestions(data) {
    if (this.childQuestions && this.childQuestions.length) {
      this.childQuestions.forEach((childQuestion) => {
        if (childQuestion.key && childQuestion.control) {
          const control = childQuestion.control;
          if (data && data[childQuestion.source]) {
            control.reset(data[childQuestion.source]);
          } else {
            control.reset(null, { onlySelf: true, emitEvent: false });
          }
          control.markAsDirty();
        }
      });
    }
  }

  setDisabled(disabled) {
    this.disabled = disabled;
    if (this.control) {
      if (this.disabled) {
        this.control.disable();
      } else {
        this.control.enable();
      }
    }
    if (this.childQuestions) {
      this.childQuestions.forEach((child) => child.setDisabled(disabled));
    }
  }

  setEditable(editable) {
    this.editable = editable;
    if (this.childQuestions) {
      this.childQuestions.forEach((child) => child.setEditable(editable));
    }
  }

  public get timezone(): any {
    return this._timezone;
  }
  public set timezone(value: any) {
    this._timezone = value;
  }

  public get minDate(): any {
    return this._minDate;
  }
  public set minDate(value: any) {
    this._minDate = value;
  }

  public get maxDate(): any {
    return this._maxDate;
  }
  public set maxDate(value: any) {
    this._maxDate = value;
  }

  public get disableDates(): any {
    return this._disableDates;
  }
  public set disableDates(value: any) {
    this._disableDates = value;
  }
}
